import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, ChevronRight, Minimize2, Send } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

// Predefined FAQs and Answers
const faqs = [
  {
    id: 1,
    question: "How do I become a member?",
    answer: "Just by signing up on our website, you can select a plan (1, 3, 6, or 12 months) and activate your membership to start borrowing books!",
    links: [
      { text: "View Membership Plans", url: "/membershipPlans" },
      { text: "Sign Up Now", url: "/signup" }
    ]
  },
  {
    id: 2,
    question: "Are these E-Books or Physical books?",
    answer: "We deliver physical copies of books directly to your doorstep for an authentic reading experience.",
  },
  {
    id: 3,
    question: "How many books can I read in one plan?",
    answer: "You can borrow one book at a time depending on your plan. Once you finish reading, return it to request a new one.",
  },
  {
    id: 4,
    question: "Is there a delivery fee?",
    answer: "No! All deliveries and returns are completely free of charge for all our members.",
  },
  {
    id: 5,
    question: "What if I damage or lose a book?",
    answer: "If a book is lost or damaged beyond repair, you will be charged the cost of the book.",
  },
  {
    id: 6,
    question: "My query isn't solved. I want to contact support.",
    answer: "No worries! I'm still learning. You can easily reach out to our human support team for further assistance.",
    isContactOption: true,
  },
];

const initialMessage = {
  id: 0,
  sender: "bot",
  text: "Hi there! I'm Booki 🦉, your reading companion. How can I help you today?",
  options: faqs, // Show options initially
};

const BookiChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcomeBubble, setShowWelcomeBubble] = useState(true);
  const [messages, setMessages] = useState([initialMessage]);
  const [isTyping, setIsTyping] = useState(false);
  const [askedQuestionIds, setAskedQuestionIds] = useState([]);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcomeBubble(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Hide the chatbot on admin and delivery dashboard routes
  const isHiddenRoute = [
    '/admin', '/categoryManager', '/authorManager', '/bookManager', 
    '/bookForm', '/revenue-manager', '/bannerManager', '/packageManager', 
    '/adminOrderRequests', '/adminReturnRequests', '/book-order-history', 
    '/database-backup', '/book-suggestions', '/book-donations', 
    '/user-manager', '/subscribed-users', '/offer-manager', '/coupon-manager', 
    '/notification-manager', '/notifications-center', '/inventory-manager', 
    '/city-manager', '/expiring-subscribers', '/bookswithusers', 
    '/expired-subscribers', '/investor-enquiries', '/activity-log', 
    '/admin-users', '/delivery-manage', '/testimonial-manager', 
    '/blog-manager', '/delivery', '/expense-manager', '/admin-cancelled-orders'
  ].some(route => location.pathname.toLowerCase().startsWith(route.toLowerCase()));

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, isOpen]);

  if (isHiddenRoute) return null;

  const handleOptionClick = (faq) => {
    // Hide the selected option
    setAskedQuestionIds((prev) => [...prev, faq.id]);

    // 1. Add user's selected question as a message
    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: faq.question,
    };
    
    // Remove options from the previous bot message so they don't clutter the screen
    setMessages((prev) => {
      const newMessages = [...prev];
      if (newMessages.length > 0) {
        newMessages[newMessages.length - 1].options = null;
      }
      return [...newMessages, userMsg];
    });

    // 2. Show typing indicator
    setIsTyping(true);

    // 3. After a delay, show bot's answer
    setTimeout(() => {
      const botMsg = {
        id: Date.now() + 1,
        sender: "bot",
        text: faq.answer,
        options: null,
        isContactOption: faq.isContactOption,
        links: faq.links,
      };
      setIsTyping(false);
      setMessages((prev) => [...prev, botMsg]);

      // 4. After another small delay, offer the "Ask another question" prompt
      setTimeout(() => {
        const followUpMsg = {
          id: Date.now() + 2,
          sender: "bot",
          text: "Is there anything else you'd like to know?",
          options: faqs, // Show the options again
        };
        setMessages((prev) => [...prev, followUpMsg]);
      }, 1000);

    }, 1500); // 1.5 second typing simulation
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end font-nunito">
      {/* Chat Window */}
      <div
        className={`bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden transition-all duration-500 origin-bottom-right flex flex-col border border-gray-100 dark:border-gray-800 ${
          isOpen
            ? "opacity-100 scale-100 mb-4 h-[500px] max-h-[80vh] w-[350px] sm:w-[400px]"
            : "opacity-0 scale-0 h-0 w-0 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-4 flex items-center justify-between text-gray-900 dark:text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800 p-0.5 overflow-hidden border border-gray-200 dark:border-gray-700">
                <img
                  src="/images/booki.jpg"
                  alt="Booki"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></span>
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">Booki</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Customer Support</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors focus:outline-none"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 dark:bg-gray-900/50 space-y-4">
          {messages.map((msg, index) => (
            <div key={msg.id} className="flex flex-col">
              {/* Message Bubble */}
              <div
                className={`flex w-full ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {msg.sender === "bot" && (
                  <img
                    src="/images/booki.jpg"
                    alt="Booki"
                    className="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 mr-2 self-end mb-1 shrink-0"
                  />
                )}
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    msg.sender === "user"
                      ? "bg-orange-500 text-white rounded-br-sm"
                      : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-700 rounded-bl-sm"
                  }`}
                >
                  {msg.text}
                  {msg.links && msg.links.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.links.map((link, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setIsOpen(false);
                            navigate(link.url);
                          }}
                          className="text-xs font-semibold bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-3 py-1.5 rounded-lg border border-orange-200 dark:border-orange-800/50 hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors flex items-center gap-1"
                        >
                          {link.text}
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                  {msg.isContactOption && (
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          navigate('/contact-us');
                        }}
                        className="w-full text-center bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 text-orange-600 dark:text-orange-400 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Go to Contact Us
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Options (Chips) */}
              {msg.options && msg.options.filter(opt => !askedQuestionIds.includes(opt.id)).length > 0 && (
                <div className="flex flex-col gap-2 mt-3 ml-10">
                  {msg.options
                    .filter((opt) => !askedQuestionIds.includes(opt.id))
                    .map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => handleOptionClick(opt)}
                      className="group flex items-center justify-between text-left px-4 py-2 bg-orange-50 hover:bg-orange-100 dark:bg-orange-500/10 dark:hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 rounded-xl text-sm font-medium transition-all"
                    >
                      <span>{opt.question}</span>
                      <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-orange-500" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex w-full justify-start mt-2">
              <img
                src="/images/booki.jpg"
                alt="Booki"
                className="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 mr-2 self-end mb-1 shrink-0"
              />
              <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area (Disabled placeholder since it's button-based) */}
        <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-gray-400 text-sm cursor-not-allowed">
            <span>Select a question above to chat...</span>
          </div>
          <div className="text-center mt-2">
            <span className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">Powered by Booki</span>
          </div>
        </div>
      </div>

      {/* Welcome Popup Bubble */}
      {!isOpen && showWelcomeBubble && (
        <div 
          onClick={() => setIsOpen(true)}
          className="absolute bottom-20 right-0 mb-2 whitespace-nowrap bg-white dark:bg-gray-800 rounded-2xl rounded-br-sm shadow-xl border border-gray-200 dark:border-gray-700 py-2.5 px-4 cursor-pointer transform origin-bottom-right transition-all hover:scale-105 group"
          style={{ animation: "float 3s ease-in-out infinite" }}
        >
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 m-0">
            👋 Hey, I'm Booki! Need help? Let's chat.
          </p>
        </div>
      )}

      {/* Floating Launcher Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group relative flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-orange-500/50 ${
          isOpen ? "" : "bg-orange-500 animate-pulse-slow"
        }`}
        style={{
          animation: !isOpen ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite" : "none"
        }}
      >
        {/* Always render the character image */}
        <div className="absolute inset-0 w-full h-full rounded-full overflow-hidden border-2 border-white">
           <img src="/images/booki.jpg" alt="Chat with Booki" className="w-full h-full object-cover" />
        </div>

        {/* Blurred overlay with X icon when chat is open */}
        {isOpen && (
          <div className="absolute inset-0 w-full h-full rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border-2 border-white/20 transition-all duration-300">
            <X className="w-8 h-8 text-white drop-shadow-md" />
          </div>
        )}
        
        {/* Unread Badge indicator */}
        {!isOpen && (
          <span className="absolute top-0 right-0 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-white"></span>
          </span>
        )}
      </button>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.4); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(249, 115, 22, 0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default BookiChatBot;
